// Peace Love Wellness — published insurance plans (single source).
//
// Every plan list on the site derives from the per-clinician
// *InsuranceAccepted arrays in ./clinicianProfiles.js: the Insurance & Fees
// page, insurance landing pages, location and specialty copy, schema, the
// Get Matched and Our Clinicians filters, and the contact form labels.
// Static files (llms.txt, practice-facts.json) can't import this module, so
// src/tests/insurance.test.js checks them against it.

import { clinicianProfiles } from './clinicianProfiles.js';
import { INTAKE_INSURANCE_OPTIONS, INTAKE_PLAN_ALIASES } from '../utils/clinicianEligibility.js';

const profiles = Object.values(clinicianProfiles);

// Display order for published lists. `partOf` plans route exactly like their
// parent (the test enforces identical clinician lists) and are named inside
// the parent's entry in prose summaries.
export const PLAN_CATALOG = [
  { name: 'Aetna' },
  { name: 'Anthem / Empire BlueCross' },
  { name: 'Excellus BCBS', partOf: 'Anthem / Empire BlueCross', short: 'Excellus' },
  { name: 'Highmark BCBS of NY', partOf: 'Anthem / Empire BlueCross', short: 'Highmark' },
  { name: 'Out-of-state BCBS (BlueCard)', partOf: 'Anthem / Empire BlueCross', short: 'out-of-state BlueCard' },
  { name: 'Carelon' },
  { name: 'Cigna' },
  { name: 'EmblemHealth' },
  { name: 'MagnaCare' },
  { name: 'Meritain' },
  { name: 'NYSHIP / The Empire Plan' },
  { name: 'NYCE PPO' },
  { name: 'United HealthCare / Optum' },
  { name: 'Oxford' },
  { name: 'Oscar' },
  { name: 'UMR' },
];

// Plans no clinician is in-network with. Published so people get a fast,
// clear answer instead of an inquiry that ends in "partial fit".
export const NOT_IN_NETWORK = ['MetroPlus', 'Medicaid', 'Medicare'];

export function joinList(items, conjunction = 'and') {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, ${conjunction} ${items.at(-1)}`;
}

export function cliniciansFor(plan) {
  return profiles.filter((p) => p.insuranceAccepted.includes(plan)).map((p) => p.firstName);
}

// Clinicians who aren't in-network but can see clients on a plan who have
// out-of-network benefits (billed out-of-network; client pays their in-network copay).
export function outOfNetworkCliniciansFor(plan) {
  return profiles.filter((p) => (p.outOfNetworkAccess || []).includes(plan)).map((p) => p.firstName);
}

export const PLANS = PLAN_CATALOG.map((plan) => ({
  ...plan,
  clinicians: cliniciansFor(plan.name),
  outOfNetwork: outOfNetworkCliniciansFor(plan.name),
}));
export const PLAN_NAMES = PLANS.map((p) => p.name);

// Plans only one clinician takes, e.g. { plan: 'Oxford', clinician: 'Cameron' }.
export const SINGLE_CLINICIAN_PLANS = PLANS.filter((p) => p.clinicians.length === 1).map((p) => ({
  plan: p.name,
  clinician: p.clinicians[0],
}));

// "— Cameron only", or "— Cameron, or Ryn with out-of-network benefits".
function routeNote(plan) {
  const clinicians = cliniciansFor(plan);
  const outOfNetwork = outOfNetworkCliniciansFor(plan);
  if (clinicians.length !== 1) return '';
  return outOfNetwork.length
    ? ` — ${clinicians[0]}, or ${joinList(outOfNetwork, 'or')} with out-of-network benefits`
    : ` — ${clinicians[0]} only`;
}

// Option text wherever a plan is offered as a choice.
export function planLabel(plan) {
  return `${plan}${routeNote(plan)}`;
}

// Contact-form checkbox label. The VALUE never changes (automations match on
// it); only the visible text gains "— Cameron only" or the Blue plans it covers.
export function intakeOptionLabel(value) {
  const plan = INTAKE_PLAN_ALIASES[value] || value;
  if (!PLAN_NAMES.includes(plan)) return value;
  if (routeNote(plan)) return `${value}${routeNote(plan)}`;
  const covered = PLANS.filter((p) => p.partOf === plan && !INTAKE_INSURANCE_OPTIONS.includes(p.name));
  return covered.length ? `${value} — includes ${joinList(covered.map((p) => p.short))} plans` : value;
}

// Plan names with their Blue partner plans folded in, for prose.
function withPartners(plan) {
  const partners = PLANS.filter((p) => p.partOf === plan.name).map((p) => p.short);
  return partners.length ? `${plan.name} (including ${joinList(partners)} plans)` : plan.name;
}

const topLevel = PLANS.filter((p) => !p.partOf);
export const GROUP_PLANS = topLevel.filter((p) => p.clinicians.length > 1);

// Sentences reused across published copy.
export const GROUP_PLANS_TEXT = joinList(GROUP_PLANS.map(withPartners));
export const NETWORK_SENTENCE = `We are in-network with ${GROUP_PLANS_TEXT}.`;
export const SINGLE_CLINICIAN_SENTENCE = Object.entries(
  SINGLE_CLINICIAN_PLANS.reduce((byClinician, { plan, clinician }) => {
    byClinician[clinician] = [...(byClinician[clinician] || []), plan];
    return byClinician;
  }, {})
)
  .map(([clinician, plans]) => `${clinician} is also in-network with ${joinList(plans)}.`)
  .join(' ');
// "Ryn can also see clients with Oxford or UMR who have out-of-network benefits: …"
// Limited to the given plans (default: every plan).
export function outOfNetworkSentence(planOrPlans = PLAN_NAMES) {
  const plans = [].concat(planOrPlans);
  return profiles
    .map((p) => [p.firstName, plans.filter((plan) => (p.outOfNetworkAccess || []).includes(plan))])
    .filter(([, covered]) => covered.length)
    .map(
      ([name, covered]) =>
        `${name} can also see clients with ${joinList(covered, 'or')} who have out-of-network benefits: ` +
        `${name} bills out-of-network, and you pay your in-network copay.`
    )
    .join(' ');
}
export const OUT_OF_NETWORK_SENTENCE = outOfNetworkSentence();
export const NOT_IN_NETWORK_SENTENCE = `We are not in-network with ${joinList(NOT_IN_NETWORK, 'or')} plans.`;
export const PLANS_SUMMARY = [NETWORK_SENTENCE, SINGLE_CLINICIAN_SENTENCE, OUT_OF_NETWORK_SENTENCE].filter(Boolean).join(' ');

// Clinicians whose list is narrower than the group's, e.g. "Clark is in-network with Carelon and Cigna only."
export const LIMITED_CLINICIAN_SENTENCE = profiles
  .filter((p) => GROUP_PLANS.some((plan) => !p.insuranceAccepted.includes(plan.name)))
  .map((p) => `${p.firstName} is in-network with ${joinList(p.insuranceAccepted)} only.`)
  .join(' ');

// "All of our clinicians except Clark are in-network with EmblemHealth."
// Takes one plan or several (clinicians in-network with all of them), and
// adds any out-of-network option for those plans.
export function planCliniciansSentence(planOrPlans) {
  return [inNetworkSentence(planOrPlans), outOfNetworkSentence(planOrPlans)].filter(Boolean).join(' ');
}

function inNetworkSentence(planOrPlans) {
  const plans = [].concat(planOrPlans);
  const clinicians = profiles
    .filter((p) => plans.every((plan) => p.insuranceAccepted.includes(plan)))
    .map((p) => p.firstName);
  const missing = profiles.map((p) => p.firstName).filter((name) => !clinicians.includes(name));
  const planText = joinList(plans);
  if (!missing.length) return `All of our clinicians are in-network with ${planText}.`;
  if (!clinicians.length) return `None of our clinicians are in-network with ${planText}.`;
  if (clinicians.length === 1) return `Only ${clinicians[0]} is in-network with ${planText}.`;
  if (missing.length < clinicians.length) {
    return `All of our clinicians except ${joinList(missing)} are in-network with ${planText}.`;
  }
  return `${joinList(clinicians)} are in-network with ${planText}.`;
}

// Our Clinicians card note, e.g. Ryn's out-of-network option.
export function outOfNetworkNote(profile) {
  const plans = profile.outOfNetworkAccess || [];
  return plans.length
    ? `Also sees clients with ${joinList(plans, 'or')} who have out-of-network benefits; you pay your in-network copay.`
    : '';
}

// Schema / practice-facts network names (every top-level plan).
export const PUBLISHED_NETWORKS = topLevel.map((p) => p.name);

// Static files (llms.txt, practice-facts.json) — insurance.test.js checks
// that they contain exactly these values.
export const PLANS_BY_CLINICIAN = Object.fromEntries(profiles.map((p) => [p.fullName, p.insuranceAccepted]));
export const LLMS_INSURANCE_SUMMARY = `${PLANS_SUMMARY} ${LIMITED_CLINICIAN_SENTENCE} ${NOT_IN_NETWORK_SENTENCE}`;
export const OUT_OF_NETWORK_BY_CLINICIAN = Object.fromEntries(
  profiles.filter((p) => p.outOfNetworkAccess?.length).map((p) => [p.fullName, p.outOfNetworkAccess])
);
export const LLMS_PLANS_BY_CLINICIAN = profiles.map((p) => {
  const outOfNetwork = p.outOfNetworkAccess?.length
    ? `; also out-of-network for ${p.outOfNetworkAccess.join(', ')} (client pays their in-network copay)`
    : '';
  return `- ${p.fullName}: ${p.insuranceAccepted.join(', ')}${outOfNetwork}`;
});
export const FACTS_INSURANCE_NOTE = `${LLMS_INSURANCE_SUMMARY} Coverage varies by plan. Confirm via the Insurance & Fees page.`;
